import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getStopByCoordinates,
} from "./mobileApis";
import backArrow from "../../../images/mobileStop/arrow_down.svg";
import Info_Banner from "../../../images/mobileStop/info_banner.svg";

import config from "../../../config.json";
import useSSE from "../../../hooks/useSSE";
import useBusRefresh from "./useBusRefresh";
import { appendMovementPoint, stableETA } from "./etaUtils";
import {
  buildStopDistances,
  isVehicleInService,
} from "./mobileScreenUtils";
import './BusList.css';
import { orderedCoordinates, journeyDirection, routeAllowsJourney, vehicleJourney } from './journeyUtils';
import { useTranslation } from "react-i18next";

/* --------------------------------------------------------------
   CONSTANTS
   -------------------------------------------------------------- */
const STOP_RADIUS_METERS = 60;
const EMPTY_ROUTES = [];
const ARRIVING_DISTANCE_METERS = 500;

/* --------------------------------------------------------------
   MAIN COMPONENT
   -------------------------------------------------------------- */
export default function BusList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const {
    originStop,
    destinationStop,
    organizationId,
    routesWithVehicles: suppliedRoutes = EMPTY_ROUTES,
    chargingStations = EMPTY_ROUTES,
  } = location.state || {};

  const routesWithVehicles = useMemo(() => suppliedRoutes.map(route => ({
    ...route, routeCoordinates: orderedCoordinates(route),
  })), [suppliedRoutes]);

  // ---------- STATE ----------
  const [currentOrigin, setCurrentOrigin] = useState(originStop);
  const [currentDestination, setCurrentDestination] = useState(destinationStop);
  const [upcomingBuses, setUpcomingBuses] = useState([]);
  const [loadingBuses, setLoadingBuses] = useState(true);
  const [routeStops, setRouteStops] = useState([]);
  const [loadingStops, setLoadingStops] = useState(true);
  const [liveData, setLiveData] = useState({});
  const [stopDistances, setStopDistances] = useState({});
  const [directions, setDirections] = useState({});
  const [statuses, setStatuses] = useState(null);

  // ---------- REFS ----------
  const vehicleSpeedHistory = useRef({});
  const previousETAsRef = useRef({});

  console.log("BusList Render:", { routeStops, currentOrigin, currentDestination });

  useEffect(() => {
    previousETAsRef.current = {};
  }, [currentOrigin, currentDestination]);

  // ---------- DEVICE IDS ----------
  const deviceIds = useMemo(() => {
    const ids = [];
    routesWithVehicles.forEach(r => {
      (r.vehicles || [])
        .forEach(v => {
          const id =
            v.deviceId ||
            v.device_id ||
            (v.device && v.device.id) ||
            v.id;
          if (id && !ids.includes(id)) ids.push(id);
        });
    });
    return ids;
  }, [routesWithVehicles]);

  // ---------- SSE ----------
  const handleSSE = useCallback(msg => {
    if (msg.type === "connected") return;
    const deviceId = msg.device_id ?? msg.vehicleId ?? msg.deviceId;
    const lat = Number(msg.Latitude ?? msg.latitude);
    const lng = Number(msg.Longitude ?? msg.longitude);
    const speed = Number(msg.speed ?? msg.Speed);

    if (!deviceId || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

    setLiveData(prev => ({
      ...prev,
      [deviceId]: {
        latitude: isNaN(lat) ? null : lat,
        longitude: isNaN(lng) ? null : lng,
        speedKph: isNaN(speed) ? null : speed,
        receivedAt: new Date().toISOString(),
      },
    }));

  }, []);

  useSSE(deviceIds, config.baseURL, handleSSE, { reconnect: true });

  // Poll both sources even when the streaming connection is quiet.
  useBusRefresh(deviceIds, setLiveData, setDirections, organizationId, setStatuses);

  // -----------------------------------------------------------------
  // --------------------- STOP DISTANCES ----------------------------
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!routesWithVehicles.length || !currentOrigin || !currentDestination) return;
    const stops = [currentOrigin, currentDestination];
    const table = buildStopDistances(routesWithVehicles, stops);
    setStopDistances(table);
  }, [routesWithVehicles, currentOrigin, currentDestination]);

  // -----------------------------------------------------------------
  // --------------------- JOURNEY DIRECTION ------------------------
  // -----------------------------------------------------------------
  const getJourneyDirection = useCallback(() => {
    if (!currentOrigin || !currentDestination || !routesWithVehicles.length) return null;

    const route = routesWithVehicles.find(r => routeAllowsJourney(r, currentOrigin, currentDestination));
    return route ? journeyDirection(route.routeCoordinates, currentOrigin, currentDestination) : null;
  }, [currentOrigin, currentDestination, routesWithVehicles]);

  // -----------------------------------------------------------------
  // --------------------- WARNING MESSAGE LOGIC --------------------
  // -----------------------------------------------------------------

  const normalizeStopName = (name) => {
    if (!name) return '';
    return name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' '); // Replace multiple spaces with single space
  };

  // Update constants to normalized versions
  const SPECIAL_ORIGINS = ["p3-parking"];
  const SPECIAL_DESTINATIONS = ["bial t1-departure"]; // Single space in normalized form
  const MANIPAL_HOSPITAL = "manipal hospital";

  // Update warningMessage useMemo:
  const warningMessage = useMemo(() => {
    const originName = normalizeStopName(currentOrigin?.name);
    const destName = normalizeStopName(currentDestination?.name);
    const journeyDir = getJourneyDirection();

    // Case 1: P3-Parking → BIAL T1-Departure
    if (SPECIAL_ORIGINS.includes(originName) && SPECIAL_DESTINATIONS.includes(destName)) {
      return "Shuttles might not be available from P3-Parking to BIAL T1-Departure. Please communicate with the driver.";
    }

    // Case 2: source_to_dest + Manipal Hospital
    if (journeyDir === "source_to_dest" && destName === MANIPAL_HOSPITAL) {
      return "Shuttles might not be available to Manipal Hospital. Please communicate with the driver.";
    }

    // Case 3: dest_to_source + BIAL T1-Departure
    if (journeyDir === "dest_to_source" && SPECIAL_DESTINATIONS.includes(destName)) {
      return "Shuttles cannot go to BIAL T1-Departure when the vehicle is returning to . Please communicate with the driver.";
    }

    return null;
  }, [currentOrigin, currentDestination, getJourneyDirection]);

  const shouldHideBuses = useMemo(() => {
    const originName = normalizeStopName(currentOrigin?.name);
    const destName = normalizeStopName(currentDestination?.name);
    if (originName === "p3-parking" && destName === "bial t1-departure") return true;
    // Apply the same route eligibility as the destination picker, including
    // its adjacent-stop exceptions.
    return !routesWithVehicles.some(route => routeAllowsJourney(route, currentOrigin, currentDestination));
  }, [currentOrigin, currentDestination, routesWithVehicles]);

  // -----------------------------------------------------------------
  // --------------------- FETCH STOP NAMES -------------------------
  // -----------------------------------------------------------------
  useEffect(() => {
    let mounted = true;
    async function fetchStops() {
      if (!routesWithVehicles.length || !organizationId) {
        if (mounted) { setRouteStops([]); setLoadingStops(false); }
        return;
      }
      const firstRoute = routesWithVehicles.find(r => routeAllowsJourney(r, currentOrigin, currentDestination));
      const coordinates = firstRoute?.routeCoordinates || [];
      if (!coordinates.length) {
        if (mounted) { setRouteStops([]); setLoadingStops(false); }
        return;
      }
      if (mounted) setLoadingStops(true);

      try {
        const promises = coordinates.map(async (coord, idx) => {
          try {
            const stop = await getStopByCoordinates(
              coord.longitude,
              coord.latitude,
              organizationId
            );
            if (stop?.name) {
              return {
                name: stop.name,
                latitude: coord.latitude,
                longitude: coord.longitude,
                originalIndex: idx,
                id: stop.id,
              };
            }
          } catch (_) { }
          return null;
        });

        const results = await Promise.all(promises);
        const filtered = results.filter(Boolean);
        filtered.sort((a, b) => a.originalIndex - b.originalIndex);

        const same = (s, t) => {
          if (s.id && t.id) return s.id === t.id;
          return (
            s.name === t.name ||
            (Math.abs(s.latitude - t.latitude) < 0.0001 &&
              Math.abs(s.longitude - t.longitude) < 0.0001)
          );
        };

        // -----------------------------------------------------------------
        // FILTER SPECIAL STOPS (GLOBAL FILTER)
        // -----------------------------------------------------------------
        let processedStops = filtered;
        if (currentOrigin && filtered.length > 0) {
          const oIdxBeforeFilter = filtered.findIndex(s => same(s, currentOrigin));
          
          if (oIdxBeforeFilter !== -1) {
            processedStops = filtered.filter((stop, idx) => {
              const stopName = normalizeStopName(stop.name);
              const isManipal = stopName === "manipal hospital";
              const isBialDeparture = stopName === "bial t1-departure";

              if (!isManipal && !isBialDeparture) return true;

              // Found special stop in list
              const stopIdx = idx;
              let shouldShow = true;

              if (isManipal) {
                  // Rule 1: Manipal restricted in source_to_dest (Origin < Stop)
                  // Allowed if: Adjacent (Origin = Stop - 1) OR dest_to_source (Origin > Stop)
                  const isSourceToDest = oIdxBeforeFilter < stopIdx;
                  const isAdjacent = oIdxBeforeFilter === (stopIdx - 1);
                  
                  if (isSourceToDest && !isAdjacent) {
                      shouldShow = false;
                      console.log("Filtered Manipal Hospital (Source->Dest, Not Adjacent)");
                  }
              } else if (isBialDeparture) {
                  // Rule 2: BIAL restricted in dest_to_source (Origin > Stop)
                  // Allowed if: Adjacent (Origin = Stop + 1) OR source_to_dest (Origin < Stop)
                  const isDestToSource = oIdxBeforeFilter > stopIdx;
                  const isAdjacent = oIdxBeforeFilter === (stopIdx + 1);

                  if (isDestToSource && !isAdjacent) {
                      shouldShow = false;
                      console.log("Filtered BIAL T1-Departure (Dest->Source, Not Adjacent)");
                  }
              }

              return shouldShow;
            });
          }
        }

        let stopsToShow = processedStops;
        let remainingStops = [];

        if (currentOrigin && currentDestination) {
          const oIdx = processedStops.findIndex(s => same(s, currentOrigin));
          const dIdx = processedStops.findIndex(s => same(s, currentDestination));
          if (oIdx !== -1 && dIdx !== -1) {
            if (oIdx < dIdx) {
              stopsToShow = processedStops.slice(oIdx, dIdx + 1);
              remainingStops = processedStops.slice(dIdx + 1);
            } else {
              stopsToShow = processedStops.slice(dIdx, oIdx + 1).reverse();
              remainingStops = processedStops.slice(0, dIdx).reverse();
            }
          }
        }

        let lastStopOriginalIndex;
        if (currentOrigin && currentDestination) {
          const oIdx = processedStops.findIndex(s => same(s, currentOrigin));
          const dIdx = processedStops.findIndex(s => same(s, currentDestination));
          if (oIdx !== -1 && dIdx !== -1) {
            lastStopOriginalIndex = oIdx < dIdx
              ? processedStops[processedStops.length - 1]?.originalIndex
              : processedStops[0]?.originalIndex;
          } else {
            lastStopOriginalIndex = processedStops[processedStops.length - 1]?.originalIndex;
          }
        } else {
          lastStopOriginalIndex = processedStops[processedStops.length - 1]?.originalIndex;
        }

        stopsToShow = stopsToShow.map(s => {
          const labelParts = [];
          const isO = currentOrigin && same(s, currentOrigin);
          const isD = currentDestination && same(s, currentDestination);
          const isLast = s.originalIndex === lastStopOriginalIndex;

          if (isO) labelParts.push({ text: "Leaving from", className: "leaving" });
          if (isD) labelParts.push({ text: "Going To", className: "going" });
          if (isLast) labelParts.push({ text: "Last Stop", className: "last" });

          return { ...s, labelParts };
        });

        remainingStops = remainingStops.map((s) => {
          const isLast = s.originalIndex === lastStopOriginalIndex;
          return {
            ...s,
            labelParts: isLast ? [{ text: "Last Stop", className: "last" }] : [],
          };
        });

        const all = [...stopsToShow, ...remainingStops];
        if (mounted) setRouteStops(all);
      } catch (err) {
        console.error("Error fetching stops:", err);
        if (mounted) setRouteStops([]);
      } finally {
        if (mounted) setLoadingStops(false);
      }
    }

    fetchStops();
    return () => { mounted = false; };
  }, [routesWithVehicles, organizationId, currentOrigin, currentDestination]);

  // Both SSE and polling feed the same rolling 15-point movement history.
  useEffect(() => {
    Object.entries(liveData).forEach(([deviceId, live]) => {
      vehicleSpeedHistory.current[deviceId] = appendMovementPoint(
        vehicleSpeedHistory.current[deviceId] || [], live);
    });
  }, [liveData]);

  const calculateRealisticETA = useCallback((distanceKm, deviceId, routeId) => {
    const key = `${routeId}:${deviceId}`;
    const eta = stableETA(distanceKm, vehicleSpeedHistory.current[deviceId] || [],
      previousETAsRef.current[key], Date.now());
    previousETAsRef.current[key] = eta;
    return eta;
  }, []);

  // -----------------------------------------------------------------
  // --------------------- BUILD INITIAL BUS LIST --------------------
  // -----------------------------------------------------------------
  useEffect(() => {
    const list = [];
    for (const route of routesWithVehicles) {
      if (!routeAllowsJourney(route, currentOrigin, currentDestination)) continue;
      for (const vehicle of route.vehicles || []) {
        const deviceId = vehicle.deviceId || vehicle.device_id || vehicle.device?.id || vehicle.id;
        const live = liveData[deviceId];
        const status = organizationId ? statuses?.[deviceId] : vehicle.status;
        if (!isVehicleInService(status, live, chargingStations)) continue;
        const journey = vehicleJourney(route.routeCoordinates, currentOrigin, currentDestination,
          live, directions[deviceId], STOP_RADIUS_METERS);
        if (!journey) continue;
        const { distanceKm, atStop } = journey;
        const eta = calculateRealisticETA(distanceKm, deviceId, route.id);
        const mins = eta.smoothedETA;
        const arriving = distanceKm * 1000 < ARRIVING_DISTANCE_METERS;
        const time = new Date(Date.now() + mins * 60000).toLocaleTimeString([], {
          hour: "2-digit", minute: "2-digit",
        });
        list.push({
          deviceId, routeId: route.id, vehicleName: vehicle.name || vehicle.VehicleNumber || "Unknown",
          live, mins: atStop ? "Arrived" : arriving || mins <= 0 ? "Arriving" : `${mins} MINS`,
          time: atStop ? "At Stop" : time, sortMins: atStop ? 0 : mins,
        });
      }
    }
    setUpcomingBuses(list.sort((a, b) => a.sortMins - b.sortMins).slice(0, 10));
    setLoadingBuses(deviceIds.length > 0 && (Object.keys(directions).length === 0 || (organizationId && statuses === null)));
  }, [routesWithVehicles, currentOrigin, currentDestination, liveData, directions,
    deviceIds, statuses, organizationId, chargingStations, calculateRealisticETA]);

  const handleBackClick = () => navigate(-1);

  if (!routesWithVehicles.length) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Typography sx={{ fontSize: "18px", color: "#666", textAlign: "center" }}>
          No route data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="bus-list-container">
      {/* HEADER */}
      <Box
        sx={{
          bgcolor: "white",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Button onClick={handleBackClick} sx={{ minWidth: "auto", p: 1, color: "#333" }}>
          <img src={backArrow} alt="Back" width={18} height={18} style={{ transform: 'rotate(90deg)', color: "#183059" }} />
        </Button>
        <Typography className="header-title">Upcoming Buses</Typography>
      </Box>

      {/* ROUTE SELECTION */}
      <Box
        sx={{
          bgcolor: "white",
          p: 2,
          mt: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            from{" "}
            <span style={{ fontWeight: 600, color: "#000", margin: "5px", fontSize: "16px" }}>
              {currentOrigin?.name || "Origin"}
            </span>{" "}
            to{" "}
            <span style={{ fontWeight: 600, color: "#000", margin: "5px", fontSize: "16px" }}>
              {currentDestination?.name || "Destination"}
            </span>
          </Typography>
        </Box>
        <Button
          sx={{
            textTransform: "none",
            fontWeight: 500,
            fontSize: "16px",
            color: "#20A8E0",
          }}
          onClick={handleBackClick}
          disabled={!currentOrigin || !currentDestination}
        >
          Change
        </Button>
      </Box>

      {/* BUS CARDS */}
      <Box className="bus-cards" sx={{ p: 2 }}>
        {loadingBuses ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : shouldHideBuses ? (
          <Typography textAlign="center" color="text.secondary" py={2}>
            Sometimes, shuttles may not follow the below route, check with the drivers to confirm
          </Typography>
        ) : upcomingBuses.length === 0 ? (
          <Typography textAlign="center" color="text.secondary" py={2}>
            Usually, a shuttle arrives at this stop every 20 minutes. However, we currently do not have any information about the next shuttle. Please check again after some time.
          </Typography>
        ) : (
          upcomingBuses.map(bus => (
            <Box
              key={`${bus.routeId}-${bus.deviceId}`}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 2,
                mb: 1.5,
                bgcolor: "#FFFFFF",
                opacity: bus.live ? 1 : 0.7,
                transition: "opacity 0.3s ease",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 400,
                      color: "#000",
                      display: "flex",
                      alignItems: "center",
                      gap: 0,
                    }}
                  >
                    {bus.vehicleName}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: bus.mins === "Arrived" ? "#4CAF50" : (bus.mins === "Arriving" ? "#FF9800" : (bus.mins === "—" ? "#FFC94D" : "#FFC94D")),
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: 400,
                      color: (bus.mins === "Arrived" || bus.mins === "Arriving") ? "#FFFFFF" : "#111214",
                    }}
                  >
                    {bus.mins}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#000",
                      minWidth: "60px",
                      textAlign: "right",
                    }}
                  >
                    {bus.time}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* ROUTE SECTION */}
      <Box sx={{ bgcolor: "white", m: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: "8px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>Route</Typography>
          <Box sx={{ display: "flex", gap: 1, border: "1px solid #e0e0e0", borderRadius: "16px", padding: "4px" }}>
            <Button
              sx={{
                backgroundColor: "#20A8E0",
                textTransform: "none",
                color: "white",
                px: 2,
                py: 0.5,
                borderRadius: "16px",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "#1b94c6",
                },
              }}
            >
              Stops
            </Button>
            <Button
              sx={{
                textTransform: "none",
                color: "#666",
                px: 2,
                py: 0.5,
                fontSize: "14px",
              }}
            >
              Map
            </Button>
          </Box>
        </Box>

        {loadingStops ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : routeStops.length === 0 ? (
          <Typography textAlign="center" color="text.secondary" py={2}>
            No route stops available.
          </Typography>
        ) : (
          <Box>
            {routeStops.map((stop, i) => (
              <Box key={i} sx={{ display: "flex", gap: 2, position: "relative" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <RadioButtonUncheckedIcon
                    sx={{ fontSize: "20px", color: "#666", zIndex: 1 }}
                  />
                  {i < routeStops.length - 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "2px",
                        height: "calc(100% - 16px)",
                        bgcolor: "#e0e0e0",
                        zIndex: 0,
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ pb: 3, flex: 1 }}>
                  <Typography sx={{ fontSize: "14px", color: "#000" }}>
                    {stop.name}{" "}
                    {stop.labelParts && stop.labelParts.length > 0 && (
                      <span style={{ marginLeft: 8, fontWeight: 600, fontSize: 12 }}>
                        (
                        {stop.labelParts.map((part, idx) => (
                          <span
                            key={idx}
                            style={{
                              color: part.className === "last" ? "#EE3B2B" : "#008C23",
                              marginRight: idx < stop.labelParts.length - 1 ? 6 : 0,
                            }}
                          >
                            {part.text}
                            {idx < stop.labelParts.length - 1 ? " • " : ""}
                          </span>
                        ))}
                        )
                      </span>
                    )}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* INFO BANNER */}
      <Box className="info-box" sx={{ m: 2, p: 2, borderRadius: "8px", bgcolor: "#E8F4FA", display: "flex", alignItems: "center", gap: 2 }}>
        <img src={Info_Banner} alt="info banner" />
        <Typography className="info-text">
          {t(
            "Shuttle service halts for few minutes between the trips and shuttle may stop at charging station for few hours"
          )}
        </Typography>
      </Box>
    </Box>
  );
}
